/**
 * i18n — spec §F12 ("react-i18next; every string through t(); en.json complete,
 * fr.json stub keys") — with the stub now filled in.
 *
 * `fr.json` used to carry exactly `en.json`'s keys with English strings and the topbar's
 * `EN` button raised a toast apologising for it. Both are gone: the bundle is translated
 * and the button is a real EN/FR toggle (`app/Topbar.tsx`). `i18n.test.ts` still asserts
 * the two bundles are structurally identical, that neither carries documentation keys, and
 * — new — that no key is left in English on the French side except the named proper nouns
 * and codes.
 *
 * **The language is a user preference, not view state.** It is persisted in
 * `localStorage['lang']` and deliberately *not* in the §F5.10 permalink: a shared link
 * restores a map, and forcing the recipient's portal into the sender's language would be
 * the one part of that link that is about the sender rather than about the map. Two people
 * reading the same permalink each read it in their own language, which is the point.
 *
 * `<html lang>` follows the active language, so screen readers switch voice and the
 * browser's own hyphenation and quotation rules follow the text.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import en from '../locales/en.json';
import fr from '../locales/fr.json';

export const DEFAULT_LANGUAGE = 'en';
export const SUPPORTED_LANGUAGES = ['en', 'fr'] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];

/** Where the choice survives a reload. */
export const LANGUAGE_STORAGE_KEY = 'lang';

/** `value` if it is one of the two shipped languages, else null. */
export function asLanguage(value: string | null | undefined): Language | null {
  return SUPPORTED_LANGUAGES.includes(value as Language) ? (value as Language) : null;
}

/**
 * The language to boot in: the stored preference, else English.
 *
 * Deliberately **not** `navigator.language`. Three of the four programme countries are
 * francophone, so guessing from the browser would be right often enough to be tempting and
 * wrong in exactly the case that matters — a francophone reader on an English-configured
 * laptop, or an IFDC reviewer expecting the English portal they were sent. The portal opens
 * in English until somebody says otherwise, and then remembers.
 *
 * A `localStorage` read can throw (Safari private mode, a blocked third-party context), and
 * an unreadable preference is not a reason to fail to boot.
 */
export function storedLanguage(): Language {
  try {
    return asLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY)) ?? DEFAULT_LANGUAGE;
  } catch {
    return DEFAULT_LANGUAGE;
  }
}

void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    fr: { translation: fr },
  },
  lng: storedLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  interpolation: { escapeValue: false }, // React already escapes
  returnNull: false,
});

/** The active language, narrowed to the two the bundles cover. */
export function currentLanguage(): Language {
  return asLanguage(i18n.resolvedLanguage ?? i18n.language) ?? DEFAULT_LANGUAGE;
}

/** The language the toggle would switch *to* — there are two, so it is the other one. */
export function otherLanguage(from: Language = currentLanguage()): Language {
  return from === 'en' ? 'fr' : 'en';
}

/**
 * Switch language: i18next, `localStorage` and `<html lang>` together.
 *
 * The store write is best-effort for the reason `storedLanguage` gives — a portal that
 * refused to switch language because it could not write a preference would be trading a
 * working session for a durable one.
 */
export async function setLanguage(language: Language): Promise<void> {
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    /* preference not persisted; the session still switches */
  }
  await i18n.changeLanguage(language);
}

/**
 * Keep `<html lang>` on the active language — including the one `init` chose, which fires
 * `languageChanged` too late for a listener registered after it, hence the direct call.
 */
function syncDocumentLanguage(language: string): void {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = asLanguage(language) ?? DEFAULT_LANGUAGE;
  }
}

i18n.on('languageChanged', syncDocumentLanguage);
syncDocumentLanguage(currentLanguage());

export default i18n;
